import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import { toast } from 'react-toastify';

import { Paginated } from '../../../../adl-gen/common';
import { DbKey, makeWithDbId, WithDbId } from '../../../../adl-gen/common/db';
import { AppClient, AppUser, AppUserStatus, makeAppUser } from '../../../../adl-gen/totalinteraction/futurefit/db';
import { AppUserListEntry } from '../../../../adl-gen/totalinteraction/futurefit/frontend';
import { UserData } from '../../../../adl-gen/totalinteraction/futurefit/requests';
import { Loader, MaybeLoading } from '../../../../loader/loader';
import { Route } from '../../../../routing/app-routes';
import { PageLayoutFn } from '../../../../ui/common/page-layout/page-layout';
import { toastOptions } from '../../common/form';
import { ListView } from '../../common/list/list-view';
import { makeAppUserListView } from '../../components/lists/appuser-list/appuser-list-view';

interface AppUserScreenProps {
  pageLayout: PageLayoutFn;
  navigateTo(route: Route): void;

  users: MaybeLoading<Paginated<AppUserListEntry>>;
  userData: MaybeLoading<UserData>;
}

@observer
export class AppUserScreen extends React.Component<AppUserScreenProps> {
  @observable.ref isOpen = false;

  @action toggleOpen(): void {
    this.isOpen = !this.isOpen;
  }

  @action
  onCancel = () => {
    this.toggleOpen();
  };

  onSelectUser = (appClientId: DbKey<AppClient>) => async (appUserId: DbKey<AppUser>) => {
    this.props.navigateTo({
      route: 'admin',
      page: 'user',
      appClientId,
      id: appUserId
    });
  };

  onCreateUser = (appClientId: DbKey<AppClient>) => () => {
    this.props.navigateTo({
      route: 'admin',
      page: 'user',
      appClientId,
      view: 'new'
    });
  };

  onUpload = (appClientId: DbKey<AppClient>) => () => {
    this.props.navigateTo({
      route: 'admin',
      page: 'user',
      appClientId,
      view: 'upload'
    });
  };

  /// HACK:
  /// Called this because it's a generic onChange but really we're only using it for the 'status' field
  onChangeStatusInList = async (statusFieldName: string, userId: string) => {
    const { users, userData } = this.props;
    /// Check this is correct
    if (statusFieldName === 'status') {
      /// Find current status of user - assuming loaded
      if (users.kind === 'value' && userData.kind === 'value') {
        const userEntry = users.value.items.find(u => u.id === userId);
        if (userEntry) {
          const user = makeWithDbId({
            id: userEntry.id,
            value: makeAppUser({
              appClientId: userData.value.appClient.id,
              fullname: userEntry.fullname,
              email: userEntry.email,
              role: userEntry.role,
              status: userEntry.status
            })
          });
          if (user.value.status === AppUserStatus.active) {
            console.log('DEBUG: onChangeStatusInList userId ', userId, ' is currently active. TODO Set to suspended');
            user.value.status = AppUserStatus.suspended;
            await this.onUpdate(user, false);
          } else {
            console.log('DEBUG: onChangeStatusInList userId ', userId, ' is currently inactive. TODO Set to active');
            user.value.status = AppUserStatus.active;
            await this.onUpdate(user, true);
          }
        } else {
          console.log('DEBUG: onChangeStatusInList userId ', userId, ': users are loaded but user not found (?)');
        }
      } else {
        console.log('DEBUG: onChangeStatusInList userId ', userId, ': users and/or userData are not loaded');
      }
    } else {
      console.log('DEBUG: onChangeStatusInList userId ', userId, ': different field invoked:', statusFieldName);
    }
  };

  /// HACK: This is really a fudge to make active content on the list view
  onUpdate = async (
    item: WithDbId<AppUser>,
    reactivating: boolean // undefined = no change, true = reactivated, false = suspended
  ) => {
    const toastId = toast.loading('Please wait...', {
      ...toastOptions,
      autoClose: 3000
    });

    try {
      toast.dismiss(toastId);
      toast.success(`User ${item.value.fullname} ${reactivating ? ' activated.' : ' suspended.'}`, {
        ...toastOptions,
        autoClose: 3000
      });
    } catch (e) {
      toast.dismiss(toastId);
      toast.error(`User ${item.value.fullname} update failed: ${(e as Error).message}`, toastOptions);
    }
  };

  renderUserList = observer((users: Paginated<AppUserListEntry>) => {
    const { userData } = this.props;

    if (userData.kind === 'value') {
      return (
        <ListView
          list={users}
          fields={makeAppUserListView(users.items)}
          refData={userData.value}
          onSelect={this.onSelectUser(userData.value.appClient.id)}
          onCreate={this.onCreateUser(userData.value.appClient.id)}
          otherAction={{ 
            action: this.onUpload(userData.value.appClient.id),
            iconType: 'add', 
            text: 'Upload...',
          }}
        />
      );
    } else {
      return null;
    }
  });

  UsersLoader = () => <Loader state={this.props.users} LoadedComponent={this.renderUserList} />;

  render() {
    /// Title is required to ensure subnav gets rendered
    return (
      <this.props.pageLayout title={'Administration'}>
        <this.UsersLoader />
      </this.props.pageLayout>
    );
  }
}



// WEBPACK FOOTER //
// ./src/admin/ui/screens/appuser-screen/appuser-screen.tsx